import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { v4 as uuidv4 } from 'uuid';
import { getCached, setCached } from '../caches';
import { handleSSE } from './handler';
import Cookies from 'js-cookie';
import useSWR from 'swr';

export type RequestMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export interface SSEOptions {
  isSSE?: boolean;
  onMessage?: (data: string) => void;
  onDone?: () => void;
  onError?: (err: any) => void;
  cache?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

const inFlightRequests = new Map<string, Promise<any>>();
let deviceTokenPromise: Promise<string> | null = null;

async function getDeviceToken(): Promise<string> {
  if (!deviceTokenPromise) {
    deviceTokenPromise = (async () => {
      const fp = await FingerprintJS.load();
      return (await fp.get()).visitorId;
    })();
  }
  return deviceTokenPromise;
}

function getSessionToken(): string {
  let session = Cookies.get('session_token');
  if (!session) {
    session = uuidv4();
    Cookies.set('session_token', session, { sameSite: 'Strict', secure: true });
  }
  return session;
}

function makeCacheKey(
  method: RequestMethod,
  url: URL,
  data: Record<string, any>
): string {
  const payloadKey =
    method === 'GET' || method === 'DELETE' ? '' : JSON.stringify(data);
  return `${method}:${url.toString()}:${payloadKey}`;
}

interface RequestArgs {
  method: RequestMethod;
  endpoint: string;
  data?: Record<string, any>;
  params?: Record<string, any>;
  user?: { userUUID?: string; purchase_id?: string };
  url: string;
  isImage?: boolean;
  options?: SSEOptions;
}

export async function request<T>({
  method,
  endpoint,
  data = {},
  params = {},
  user,
  url: baseUrl,
  isImage = false,
  options = {},
}: RequestArgs): Promise<T> {
  const url = new URL(`${baseUrl}${endpoint}`);

  if ((method === 'GET' || method === 'DELETE') && Object.keys(params).length) {
    url.search = new URLSearchParams(params as any).toString();
  }

  const useCache = options.cache === true;
  const cacheKey = makeCacheKey(method, url, data);

  if (useCache) {
    if (inFlightRequests.has(cacheKey)) return inFlightRequests.get(cacheKey)!;
    const cached = await getCached<T>(cacheKey);
    if (cached !== null && cached !== undefined) return cached;
  }

  const requestPromise = (async (): Promise<T> => {
    const deviceToken = await getDeviceToken();
    const sessionToken = getSessionToken();

    const headers: HeadersInit = {
      Accept: isImage ? 'image/png' : 'application/json',
      'X-Device-Token': deviceToken,
      'X-Session-Token': sessionToken,
      ...options.headers,
    };

    if (user?.userUUID) headers['X-User-UUID'] = user.userUUID;
    if (user?.purchase_id) headers['X-Purchase-Id'] = user.purchase_id;

    if (options.isSSE) {
      await handleSSE<T>(url.toString(), method, data, headers, options);
      return null as unknown as T;
    }

    if (!(data instanceof FormData))
      headers['Content-Type'] = 'application/json';

    const res = await fetch(url.toString(), {
      method,
      headers,
      body:
        method !== 'GET' && Object.keys(data).length
          ? data instanceof FormData
            ? data
            : JSON.stringify(data)
          : undefined,
      credentials: 'include',
      signal: options.signal,
    });

    const contentType = res.headers.get('Content-Type') ?? '';
    let result: T;

    if (contentType.includes('application/json')) {
      const json = await res.json();
      result = (json?.data ?? json) as T;
    } else if (contentType.includes('image/png')) {
      result = (await res.blob()) as any;
    } else {
      result = (await res.text()) as any;
    }

    if (useCache) setCached(cacheKey, result);
    return result;
  })();

  if (useCache) {
    inFlightRequests.set(cacheKey, requestPromise);
    requestPromise.finally(() => inFlightRequests.delete(cacheKey));
  }

  return requestPromise;
}

interface UseFetchArgs {
  endpoint: string;
  url: string;
  params?: Record<string, any>;
  options?: SSEOptions;
  user?: { userUUID?: string; purchase_id?: string };
}

export function useFetch<T>({
  endpoint,
  url: baseUrl,
  params = {},
  options,
  user,
}: UseFetchArgs) {
  const url = new URL(`${baseUrl}${endpoint}`);
  if (Object.keys(params).length)
    url.search = new URLSearchParams(params as any).toString();

  const { data, error } = useSWR<T>(url.toString(), () =>
    request<T>({ method: 'GET', endpoint, params, url: baseUrl, user, options })
  );

  return { data, error };
}

export interface TypedFetcher {
  request: <T>(args: RequestArgs) => Promise<T>;
  useFetch: <T>(
    args: Omit<UseFetchArgs, 'url'> & { url: string }
  ) => { data: T | undefined; error: any };
  post: <T>(
    endpoint: string,
    data?: any,
    params?: any,
    options?: SSEOptions,
    user?: { userUUID?: string; purchase_id?: string }
  ) => Promise<T>;
  patch: <T>(
    endpoint: string,
    data?: any,
    params?: any,
    options?: SSEOptions,
    user?: { userUUID?: string; purchase_id?: string }
  ) => Promise<T>;
  delete: <T>(
    endpoint: string,
    params?: any,
    options?: SSEOptions,
    user?: { userUUID?: string; purchase_id?: string }
  ) => Promise<T>;
}

export function createFetcherClient(baseUrl: string): TypedFetcher {
  return {
    request: <T>(args: Omit<RequestArgs, 'url'>) =>
      request<T>({ ...args, url: baseUrl }),
    useFetch: <T>(args: Omit<UseFetchArgs, 'url'>) =>
      useFetch<T>({ ...args, url: baseUrl }),
    post: <T>(
      endpoint: string,
      data: any = {},
      params: any = {},
      options?: SSEOptions,
      user?: { userUUID?: string; purchase_id?: string }
    ) =>
      request<T>({
        method: 'POST',
        endpoint,
        data,
        params,
        url: baseUrl,
        user,
        options,
      }),
    patch: <T>(
      endpoint: string,
      data: any = {},
      params: any = {},
      options?: SSEOptions,
      user?: { userUUID?: string; purchase_id?: string }
    ) =>
      request<T>({
        method: 'PATCH',
        endpoint,
        data,
        params,
        url: baseUrl,
        user,
        options,
      }),
    delete: <T>(
      endpoint: string,
      params: any = {},
      options?: SSEOptions,
      user?: { userUUID?: string; purchase_id?: string }
    ) =>
      request<T>({
        method: 'DELETE',
        endpoint,
        data: {},
        params,
        url: baseUrl,
        user,
        options,
      }),
  };
}